// src/views/auth/forgotPassword/index.jsx
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  AspectRatio,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Input,
  Stack,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { requestTempPassword } from "services/auth";

const heroTitleLines = ["비밀번호를 잊으셨나요?", ""];
const heroSubtitle = "가입한 이메일을 입력하면 임시 비밀번호 링크를 보내드립니다.";

export default function ForgotPassword() {
  const toast = useToast();
  const textColor = useColorModeValue("#0f172a", "white");
  const textColorSecondary = useColorModeValue("gray.600", "gray.300");
  const highlightColor = useColorModeValue("#2563eb", "#8b99ff");
  const heroFontSize = useBreakpointValue({ base: "28px", md: "32px", lg: "36px" });

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);

  const issueTempPassword = async () => {
    if (!email.trim()) {
      toast({ status: "warning", title: "@xisnd.com" });
      return;
    }
    try {
      setLoading(true);
      await requestTempPassword(email.trim());
      toast({ status: "success", title: "임시 비밀번호 발급", description: "이메일을 확인하세요." });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "요청에 실패했습니다.";
      toast({ status: "error", title: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" bgGradient="linear(to-b, #eef2ff, #fefefe)" px={{ base: 4, md: 6 }}>
      <Flex minH="100vh" align="center" justify="center" py={{ base: 6, md: 10 }}>
        <Box w="full" maxW="1024px">
          <Box
            bg="white"
            borderRadius={{ base: "24px", md: "32px" }}
            borderWidth="1px"
            borderColor="rgba(15, 23, 42, 0.08)"
            boxShadow="0 40px 90px rgba(15, 23, 42, 0.18)"
            overflow="hidden"
          >
            <Flex
              direction={{ base: "column", lg: "row" }}
              gap={{ base: 0, lg: 1 }}
              align="stretch"
            >
              <Box
                flex={{ base: "none", lg: 1 }}
                display="flex"
                p={{ base: 8, lg: 10 }}
                bg="white"
                borderRight={{ base: "none", lg: "1px solid rgba(15, 23, 42, 0.08)" }}
              >
                <Stack spacing={6} flex="1">
                  <Box>
                    <Text
                      fontSize={heroFontSize}
                      fontWeight="700"
                      lineHeight="1.2"
                      color={textColor}
                    >
                      {heroTitleLines.map((line) => (
                        <Text as="span" key={line} display="block">
                          {line}
                        </Text>
                      ))}
                    </Text>
                    <Text mt={2} color={textColorSecondary} fontSize={{ base: "13px", md: "13px" }}>
                      {heroSubtitle}
                    </Text>
                  </Box>
                  <Box
                    borderRadius="28px"
                    overflow="hidden"
                    bg="white"
                    boxShadow="0 25px 50px rgba(37, 99, 235, 0.25)"
                  >
                    <AspectRatio ratio={4 / 3}>
                      <Image
                        src="/starter.gif"
                        alt="프로젝트 스타터"
                        objectFit="cover"
                        opacity={heroLoaded ? 1 : 0}
                        transition="opacity 0.7s ease"
                        onLoad={() => setHeroLoaded(true)}
                        draggable={false}
                      />
                    </AspectRatio>
                  </Box>
                </Stack>
              </Box>

              <Box
                flex={{ base: "none", lg: 1 }}
                p={{ base: 8, md: 10 }}
                bg="white"
                display="flex"
                flexDirection="column"
                justifyContent="flex-start"
              >
                <Stack spacing={5}>
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      issueTempPassword();
                    }}
                  >
                    <Stack spacing={4}>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm" fontWeight="600" color={textColor}>
                          이메일
                        </FormLabel>
                        <Input
                          type="email"
                          placeholder="이메일을 입력하세요."
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          h="52px"
                          fontSize="md"
                          px="16px"
                          borderRadius="16px"
                          bg="gray.50"
                        />
                      </FormControl>
                      <Flex
                        justify="flex-end"
                        align="center"
                        fontSize="xs"
                        color={highlightColor}
                        pt={1}
                        gap={2}
                      >
                        <Text color="gray.500">계정을 찾으셨나요?</Text>
                        <NavLink to="/auth/sign-in/default">
                          <Text fontWeight="600" _hover={{ textDecor: "underline" }}>
                            로그인
                          </Text>
                        </NavLink>
                      </Flex>
                      <Button
                        type="submit"
                        h="52px"
                        fontSize="sm"
                        fontWeight="600"
                        isLoading={loading}
                        bgGradient="linear(to-r, #4f46e5, #1d4ed8)"
                        color="white"
                        borderRadius="16px"
                        _hover={{ bgGradient: "linear(to-r, #4338ca, #1e40af)" }}
                        boxShadow="0 20px 30px rgba(37, 99, 235, 0.25)"
                      >
                        임시 비밀번호 전송
                      </Button>
                    </Stack>
                  </form>
                </Stack>
              </Box>
            </Flex>
          </Box>
          <Text mt={6} fontSize="12px" color="gray.400" textAlign="center">
            © XI S&D, All Rights Reserved. Designed & Developed by DX Team.
          </Text>
        </Box>
      </Flex>
    </Box>
  );
}
